import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { createInvite, listInvites, cancelInvite } from '../services/supabase';
import { Invite } from '../types';
import './InviteManagement.css';

const InviteManagement: React.FC = () => {
  const { userContext } = useAuth();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  const isAdmin = userContext?.membership?.role === 'admin';
  const projectDomain = userContext?.project?.domain || '';

  const fetchInvites = useCallback(async () => {
    if (!isAdmin) return;
    setIsFetching(true);
    try {
      const result = await listInvites();
      setInvites(result.invites as Invite[]);
    } catch (err: any) {
      console.error('Failed to fetch invites:', err);
    } finally {
      setIsFetching(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Validate domain
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (emailDomain !== projectDomain) {
      setError(`Can only invite users with @${projectDomain} email addresses`);
      return;
    }

    setIsLoading(true);
    try {
      await createInvite(email.trim().toLowerCase(), role);
      setSuccess(`Invitation sent to ${email}`);
      setEmail('');
      fetchInvites();
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await cancelInvite(inviteId);
      fetchInvites();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel invitation');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      pending: 'badge-pending',
      accepted: 'badge-accepted',
      expired: 'badge-expired',
      cancelled: 'badge-cancelled',
    };
    return <span className={`invite-badge ${classes[status] || ''}`}>{status}</span>;
  };

  if (!isAdmin) {
    return (
      <div className="invite-management">
        <div className="invite-restricted">
          <p>Only admins can manage team invitations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-management">
      <div className="invite-header">
        <h2>Team Management</h2>
        <p>Invite team members to join {userContext?.project?.company_name}</p>
      </div>

      <form onSubmit={handleInvite} className="invite-form">
        {error && <div className="invite-error">{error}</div>}
        {success && <div className="invite-success">{success}</div>}

        <div className="invite-row">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder={`colleague@${projectDomain}`} disabled={isLoading}
          />
          <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'member')} disabled={isLoading}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </form>

      <div className="invites-list">
        <h3>Invitations ({invites.length})</h3>
        {isFetching ? (
          <p className="invites-loading">Loading invitations...</p>
        ) : invites.length === 0 ? (
          <p className="invites-empty">No invitations sent yet.</p>
        ) : (
          <table className="invites-table">
            <thead>
              <tr><th>Email</th><th>Role</th><th>Status</th><th>Sent</th><th>Expires</th><th></th></tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.id}>
                  <td>{invite.email}</td>
                  <td><span className={`role-badge role-${invite.role}`}>{invite.role}</span></td>
                  <td>{getStatusBadge(invite.status)}</td>
                  <td>{formatDate(invite.created_at)}</td>
                  <td>{formatDate(invite.expires_at)}</td>
                  <td>
                    {invite.status === 'pending' && (
                      <button className="cancel-btn" onClick={() => handleCancelInvite(invite.id)}>Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default InviteManagement;

